import { useTrackMarketing } from '@packages/shared/src/hooks/useTrackCampaign/useTrackCampaign';
import { useRef } from 'react';
import type { SxProps, Theme } from '@mui/material';
import { Link } from '@packages/shared/src/components/Link/Link';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import type { LinkByTextType } from './types';
import { Icon } from '../Icon/Icon';
import { SoftHyphenInjector } from '../SoftHyphenInjector';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import { ComponentTypes } from '../../../interfaces';
import { buildLinkWithArguments } from '../../utils/buildLinkWithArguments';

/**
 * LinkByText component displays link with Arguments and an optional Icon with a direction
 * */
export const LinkByText = ({
  data,
  sx,
  className,
}: {
  data: LinkByTextType;
  sx?: SxProps<Theme>;
  className?: string;
}) => {
  const {
    field_link: link,
    field_link_target: linkTarget,
    behavior_paragraph_link_rel: linkRelRaw,
    field_direction: direction,
    field_text: text,
    field_icon: icon,
  } = data;
  const linkRel = linkRelRaw === '_none' ? undefined : linkRelRaw;
  const tmpLink = buildLinkWithArguments(link, data);
  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(tmpLink);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const { trackClick } = useTrackMarketing(data, linkRef, finalLink);
  return (
    <Link
      {...(className && { className })}
      ref={linkRef}
      href={finalLink}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        color: 'text.dark',
        textDecorationColor: (theme) => theme.palette.text.dark,
        '& svg': {
          height: (theme) => theme.spacing(2),
          width: 'auto',
          ...(direction === 'left' && {
            order: '-1',
          }),
        },
        ...sx,
      }}
      onClick={() => trackClick()}
      openInNewTab={!!(linkTarget && linkTarget === '_blank')}
      rel={linkRel}
    >
      <SoftHyphenInjector text={text} />
      {icon && <Icon type={icon} />}
    </Link>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({ [ComponentTypes.LinkByText]: LinkByText });
